import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { noAnimation, Show } from "../../lib/utils";
import { fadeInAnimation } from "../../lib/animations";
import { getIsTooWrong, getLastIaQuestion, getWrongAnswers } from "./selectors";
import { iaGoToModule, updateWrongAnswers } from "./actions";
import { BackendModuleTypes } from "../../lib/types";
import { ThumbIcon } from "../../lib/icons";
import { Answer } from "../messages/Answer";

import { getLatestState, getProviderID } from "../../store";
import { postUserAction } from "./api";
import { deepChange, scrollBottom } from "../../actions";

export const Feedback = ({ data }) => {
  const { values, question } = data;
  const dispatch = useDispatch();
  const isTooWrong = useSelector(getIsTooWrong);
  const wrongAnswers = useSelector(getWrongAnswers);
  const providerID = useSelector(getProviderID);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    scrollBottom();
  }, [isTooWrong]);

  const gotoAnswerFlow = () => {
    // Save last question to userData object to skip asking user about it again.
    const state = dispatch(getLatestState);
    const lastIaQuestion = getLastIaQuestion(state);
    dispatch(deepChange({ "userData.lead_additional_information": lastIaQuestion }));

    dispatch(iaGoToModule(BackendModuleTypes.ANSWER_FLOW));
  };

  if (isTooWrong) {
    return (
      <>
        Okay, sorry we were not able to answer this question. We are going to send this question to the community to
        answer.
        <div>
          <Answer onClick={gotoAnswerFlow}>Proceed</Answer>
        </div>
      </>
    );
  }

  /**
   * Submit instant answers feedback.
   * @param feedback - boolean, was the answer useful or not.
   */
  const submitFeedback = (feedback: boolean) => {
    setFeedback(feedback);
    dispatch(updateWrongAnswers(feedback ? 0 : wrongAnswers + 1));

    const requestData = {
      answer: values,
      question,
      was_helpful: feedback,
    };
    postUserAction(requestData, providerID);
  };

  return (
    <>
      <Show when={feedback === null}>
        <FeedbackWrapper className="fadein">
          <FeedbackItem onClick={() => submitFeedback(true)}>
            <ThumbIcon />
            <FeedbackAnswerLabel>Thanks!</FeedbackAnswerLabel>
          </FeedbackItem>

          <FeedbackItem onClick={() => submitFeedback(false)}>
            <ThumbIcon inverted />
            <FeedbackAnswerLabel>Not helpful</FeedbackAnswerLabel>
          </FeedbackItem>
        </FeedbackWrapper>
      </Show>
      <Show when={feedback === true}>
        <FeedbackWrapper className="fadeout">Okay, Thanks for letting us know!</FeedbackWrapper>
      </Show>

      <Show when={feedback === false}>
        <FeedbackWrapper>
          Please retype your question below and remember to keep questions short and related to a single topic.
        </FeedbackWrapper>
      </Show>
    </>
  );
};

const FeedbackWrapper = styled.div`
  color: #595d61;
  display: flex;

  &.fadein {
    opacity: 0;
    animation: ease 0.1s ${noAnimation ? 0 : 1}s forwards ${fadeInAnimation};
  }

  &.fadeout {
    opacity: 1;
    animation: ease-out 0.2s ${noAnimation ? 0 : 2}s reverse ${fadeInAnimation};
    animation-fill-mode: forwards;
  }
`;

const FeedbackItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #595d61;
  border-radius: 5px;
  border: 1px solid #dddddd;
  padding: 6px 15px;
  margin-right: 5px;
  box-shadow: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const FeedbackAnswerLabel = styled.div`
  margin-left: 8px;
`;
